declare global {
  interface Window {
    dataLayer: any[];
  }
}

let gtmInjected = false;

export function injectGtm(containerId: string): void {
  if (gtmInjected || !containerId) return;
  gtmInjected = true;
  localStorage.setItem('gtmContainerId', containerId);
  if (document.querySelector(`script[src*="${containerId}"]`)) return;

  const script = document.createElement('script');
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${containerId}');`;
  document.head.insertBefore(script, document.head.firstChild);

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.cssText = 'display:none;visibility:hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function dl() {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

/**
 * Svi GA4 ecommerce eventi idu kroz ovo.
 *
 * GTM gradi kumulativni model podataka iz dataLayera, pa ključevi iz ranijeg
 * `ecommerce` objekta ostaju u modelu i stapaju se u sljedeći event. Bez
 * prethodnog `{ecommerce: null}` pusha, `purchase` naslijedi ostatke od
 * `add_payment_info`, koji u ovom toku ide neposredno prije njega.
 */
function pushEcommerce(event: string, ecommerce: Record<string, unknown>): void {
  dl().push({ ecommerce: null });
  dl().push({ event, ecommerce });
}

/**
 * Dedup markeri. sessionStorage baca kad je pristup skladištu blokiran
 * (privatni prozori, blokirani podaci stranice), a tada je bolje poslati
 * event nego ga tiho izgubiti — zato in-memory Set kao rezerva, koji drži
 * unutar jednog učitavanja stranice.
 */
const sentInMemory = new Set<string>();

function alreadySent(key: string): boolean {
  if (sentInMemory.has(key)) return true;
  try {
    return sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function markSent(key: string): void {
  sentInMemory.add(key);
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // Skladište nedostupno; in-memory marker je jedino što imamo.
  }
}

interface GtmItem {
  item_id: string;
  item_name: string;
  item_category: string;
  price: number;
  quantity: number;
}

const PACKAGE_IDS: Record<string, string> = {
  A: 'css-2026-paket-a',
  B: 'css-2026-paket-b',
  C: 'css-2026-paket-c',
  D: 'css-2026-paket-d',
};

const PACKAGE_NAMES: Record<string, string> = {
  A: 'CSS 2026 Paket A',
  B: 'CSS 2026 Paket B',
  C: 'CSS 2026 Paket C',
  D: 'CSS 2026 Paket D',
};

function buildItem(pkg: string, unitPrice: number, quantity: number): GtmItem {
  // Payload dolazi iz sessionStorage i može biti zatečen iz starije verzije
  // aplikacije. Nedostajuće polje ne smije srušiti slanje eventa.
  const safePkg = typeof pkg === 'string' && pkg ? pkg : '';
  return {
    item_id:
      PACKAGE_IDS[safePkg] ??
      `css-2026-paket-${safePkg.toLowerCase() || 'nepoznat'}`,
    item_name: PACKAGE_NAMES[safePkg] ?? `CSS 2026 Paket ${safePkg || '?'}`,
    item_category: 'Conference Ticket',
    price: unitPrice,
    quantity,
  };
}

export function pushBeginCheckout(pkg: string, unitPrice: number, totalValue: number): void {
  pushEcommerce('begin_checkout', {
    currency: 'BAM',
    value: totalValue,
    items: [buildItem(pkg, unitPrice, 1)],
  });
}

export function pushAddPaymentInfo(
  pkg: string,
  unitPrice: number,
  quantity: number,
  totalValue: number,
  paymentType: 'card' | 'invoice'
): void {
  pushEcommerce('add_payment_info', {
    currency: 'BAM',
    value: totalValue,
    payment_type: paymentType,
    items: [buildItem(pkg, unitPrice, quantity)],
  });
}

export interface PendingPurchaseData {
  orderNumber: string;
  bundlePackage: string;
  unitPrice: number;
  quantity: number;
  totalValue: number;
}

export const PENDING_PURCHASE_KEY = 'pending_monri_purchase';

export function pushPurchase(data: PendingPurchaseData): void {
  const sentKey = `purchase_sent_${data.orderNumber}`;
  if (alreadySent(sentKey)) return;
  markSent(sentKey);
  pushEcommerce('purchase', {
    transaction_id: data.orderNumber,
    value: data.totalValue,
    currency: 'BAM',
    payment_type: 'card',
    items: [buildItem(data.bundlePackage, data.unitPrice, data.quantity)],
  });
}

export interface PendingLeadData {
  bundlePackage: string;
  unitPrice: number;
  quantity: number;
  totalValue: number;
}

/**
 * Invoice ("predračun") registracija je uspješno kreirana na backendu.
 *
 * Namjerno NIJE `purchase`: predračunom se u Eventio administraciji kreira
 * NEODOBREN učesnik, kojeg administrator ručno odobrava tek nakon što
 * evidentira uplatu na bankovnom izvodu. `purchase` se i dalje šalje samo
 * za kartično plaćanje, iz pushPurchase().
 *
 * Dedup je odgovornost pozivaoca (consume-once ref u Register/View.tsx).
 */
export function pushGenerateLead(data: PendingLeadData): void {
  pushEcommerce('generate_lead', {
    currency: 'BAM',
    value: data.totalValue,
    payment_type: 'invoice',
    items: [buildItem(data.bundlePackage, data.unitPrice, data.quantity)],
  });
}
